import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import { has, isEmpty, omitBy } from 'lodash';
import { StateCreator } from 'zustand';

import { migrateUniqueIdentifier } from './functions/migrateUniqueIdentifier';
import { AttributionState } from './types/AttributionState';
import { AttributionStateDependencies } from './types/AttributionStateDependencies';

export const keysToPersist = ['attributions', 'initialURL', 'leadChannelCode'];

export const attributionStore =
  ({ config }: AttributionStateDependencies): StateCreator<AttributionState> =>
  (set, get) => ({
    attributions: {},
    initialURL: {},

    // this leadChannelCode is unrelated to the one found in the parameterStore.
    // its primary use here is to handle the attribution data based on whether a leadChannelCode is present in the URL.
    // whereas the parameterStore's leadChannelCode serves other purposes like tracking the source of the lead and is explicit
    // in whether it is present or not.
    leadChannelCode: config.defaultLeadChannelCode,

    computed: {
      get attributionsForLeadChannel(): Record<string, string> {
        const { attributions, leadChannelCode } = get();

        if (!leadChannelCode || !has(attributions, leadChannelCode)) {
          return {};
        }

        return attributions[leadChannelCode];
      },
      get initialURLForLeadChannel(): string {
        const { initialURL, leadChannelCode } = get();

        if (
          isNonEmptyString(leadChannelCode) &&
          has(initialURL, leadChannelCode)
        ) {
          return initialURL[leadChannelCode];
        }

        return '';
      },
      get partnerRecordUniqueIdentifier(): string | undefined {
        const { attributionsForLeadChannel } = get().computed;

        if ('partnerRecordUniqueIdentifier' in attributionsForLeadChannel) {
          return attributionsForLeadChannel.partnerRecordUniqueIdentifier;
        }

        return undefined;
      },
    },

    setFromURLSearchParams(searchParams: URLSearchParams): void {
      const entries = Object.fromEntries(searchParams.entries());

      if (isEmpty(entries)) {
        return;
      }

      if (
        has(entries, 'leadChannelCode') &&
        !isEmpty(entries.leadChannelCode)
      ) {
        set({ leadChannelCode: entries.leadChannelCode });
      }

      const { attributions, leadChannelCode } = get();

      if (has(attributions, leadChannelCode)) {
        const currentParams = attributions[leadChannelCode];

        attributions[leadChannelCode] = migrateUniqueIdentifier(
          omitBy({ ...currentParams, ...entries, leadChannelCode }, isEmpty),
        );
      } else {
        attributions[leadChannelCode] = migrateUniqueIdentifier(
          omitBy({ ...entries, leadChannelCode }, isEmpty),
        );
      }

      set({ attributions });
    },

    setInitialURL(url: URL): void {
      const { initialURL, leadChannelCode } = get();

      if (
        isNonEmptyString(leadChannelCode) &&
        !has(initialURL, leadChannelCode)
      ) {
        initialURL[leadChannelCode] = url.href;
        set({ initialURL });

        return;
      }

      // to support cases where we don't have lead channel code in state yet,
      // we can check the provided URL and use that value to set the initial URL
      const leadChannelCodeInURL = url.searchParams.get('leadChannelCode');

      if (leadChannelCodeInURL && !has(initialURL, leadChannelCodeInURL)) {
        initialURL[leadChannelCodeInURL] = url.href;
        set({ initialURL });
      }
    },
  });
